import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, EyeOff, Zap, Shield, Clock } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'client', company: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(formData);
      toast.success('Compte créé avec succès !');
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="register-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden">
        <div className="absolute inset-0 bg-[#397DFF]" />
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_price-alert-hub-4/artifacts/hg5pmymg_LOGO%20PDE%202025.png" 
              alt="Place des Energies" 
              className="h-12 w-auto"
            />
          </div>

          <div className="max-w-lg">
            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Rejoignez<br />Place des Energies
            </h1>
            <p className="text-lg text-white/80 leading-relaxed max-w-md">
              Créez votre espace pour suivre les prix de l'énergie et recevoir des recommandations personnalisées.
            </p>

            <div className="mt-10 flex flex-col gap-4">
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Scoring intelligent</p>
                  <p className="text-white/60 text-xs">Recommandations basées sur l'analyse du marché</p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Rapports PDF</p>
                  <p className="text-white/60 text-xs">Téléchargez vos rapports personnalisés</p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Récap hebdomadaire</p>
                  <p className="text-white/60 text-xs">Un résumé chaque lundi dans votre boîte mail</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-white/40 text-sm">Place des Energies &copy; {new Date().getFullYear()}</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[#F5F6F9]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_91b12b4d-28f1-45e7-a2d9-52b3b7ff0be6/artifacts/3cjn9m53_PDE_Plan%20de%20travail%201.jpg" 
              alt="Place des Energies" 
              className="h-10 w-auto mb-3"
            />
            <p className="text-[#687175] text-sm">Créez votre espace</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 border border-[#BFC6CC]/20">
            <h2 className="text-2xl font-bold text-[#191D1E] mb-1">Créer un compte</h2>
            <p className="text-[#687175] text-sm mb-6">Remplissez vos informations</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-[#191D1E] font-medium text-sm">Nom complet</Label>
                <Input
                  id="name" name="name" type="text" value={formData.name}
                  onChange={handleChange} placeholder="Jean Dupont"
                  className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E] placeholder-[#939BA0] focus:border-[#397DFF] focus:ring-[#397DFF]/20"
                  required data-testid="register-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-[#191D1E] font-medium text-sm">Email</Label>
                <Input
                  id="email" name="email" type="email" value={formData.email}
                  onChange={handleChange} placeholder="votre@email.com"
                  className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E] placeholder-[#939BA0] focus:border-[#397DFF] focus:ring-[#397DFF]/20"
                  required data-testid="register-email-input"
                />
              </div>

              <div>
                <Label htmlFor="company" className="text-[#191D1E] font-medium text-sm">Entreprise</Label>
                <Input
                  id="company" name="company" type="text" value={formData.company}
                  onChange={handleChange} placeholder="Nom de votre entreprise"
                  className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E] placeholder-[#939BA0] focus:border-[#397DFF] focus:ring-[#397DFF]/20"
                  data-testid="register-company-input"
                />
              </div>

              <div>
                <Label htmlFor="role" className="text-[#191D1E] font-medium text-sm">Type de compte</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" data-testid="register-role-select">
                    <SelectValue placeholder="Sélectionnez un type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#BFC6CC]">
                    <SelectItem value="client" className="text-[#191D1E]">Client</SelectItem>
                    <SelectItem value="admin" className="text-[#191D1E]">Chargé d'affaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="password" className="text-[#191D1E] font-medium text-sm">Mot de passe</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password" name="password" type={showPassword ? 'text' : 'password'}
                    value={formData.password} onChange={handleChange} placeholder="••••••••"
                    className="bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E] placeholder-[#939BA0] focus:border-[#397DFF] focus:ring-[#397DFF]/20 pr-10"
                    required minLength={6} data-testid="register-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#939BA0] hover:text-[#687175]">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full btn-primary h-11" disabled={loading} data-testid="register-submit-btn">
                {loading ? 'Création...' : 'Créer mon compte'}
              </Button>
            </form>

            <p className="mt-6 text-center text-[#687175] text-sm">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-[#397DFF] hover:text-[#2563eb] font-medium">Se connecter</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
